import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define the steps for the checkout process.
export type CheckoutStep = "billing" | "shipping" | "appointment" | "payment";

/**
 * Defines the shape of the checkout state managed by Redux.
 * Reduced to only UI state that must be shared globally or isn't managed by React Query / Forms.
 */
export interface CheckoutState {
  /** The currently active step in the checkout flow. */
  currentStep: CheckoutStep;

  /** The ID of the chosen payment gateway (e.g., 'bacs', 'cod', 'stripe'). */
  selectedPaymentMethod: string | null;

  /** A flag to indicate when the final 'processCheckout' mutation is in flight. */
  isProcessing: boolean;

  /** Stores server-side validation errors returned from the server. */
  serverErrors: Record<string, string> | null;

  /**
   * Determines if the shipping address should be the same as the billing address.
   * If true, the Shipping Step form is disabled and shipping address is synced from billing.
   * If false, it is editable.
   */
  useBillingForShipping: boolean;

  /**
   * The customer note/order comments.
   * Managed in client state and synced to server only on specific actions (Payment Method switch / Final Submit).
   */
  customerNote: string;

  /**
   * Temporary storage for the user's selected appointment slot before order submission.
   */
  appointmentDate: string;
  appointmentTime: string;
}

// Initialize currentStep from URL if present, otherwise default to 'billing'
const getInitialStep = (): CheckoutStep => {
  const params = new URLSearchParams(window.location.search);
  const step = params.get("step");
  const validSteps: CheckoutStep[] = [
    "billing",
    "shipping",
    "appointment",
    "payment",
  ];
  return validSteps.includes(step as CheckoutStep)
    ? (step as CheckoutStep)
    : "billing";
};

const initialState: CheckoutState = {
  currentStep: getInitialStep(),
  selectedPaymentMethod: null,
  isProcessing: false,
  serverErrors: null,
  useBillingForShipping: true, // Default to true (using same address)
  customerNote: "", // Initialize as empty string
  appointmentDate: "",
  appointmentTime: "",
};

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    /**
     * Navigates the user to a specific step in the checkout process.
     */
    setCurrentStep: (state, action: PayloadAction<CheckoutStep>) => {
      state.currentStep = action.payload;
    },
    /**
     * Sets the ID of the selected payment method.
     */
    setSelectedPaymentMethod: (state, action: PayloadAction<string | null>) => {
      state.selectedPaymentMethod = action.payload;
    },
    /**
     * Toggles the global processing state.
     */
    setIsProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },
    /**
     * Stores server-side validation errors.
     */
    setServerErrors: (
      state,
      action: PayloadAction<Record<string, string> | null>,
    ) => {
      state.serverErrors = action.payload;
    },
    /**
     * Sets whether to use the billing address for shipping.
     */
    setUseBillingForShipping: (state, action: PayloadAction<boolean>) => {
      state.useBillingForShipping = action.payload;
    },
    /**
     * Updates the customer note in the client state.
     */
    setCustomerNote: (state, action: PayloadAction<string>) => {
      state.customerNote = action.payload;
    },
    /**
     * Updates the temporary appointment selection.
     */
    setAppointmentSelection: (
      state,
      action: PayloadAction<{ date: string; time: string }>,
    ) => {
      state.appointmentDate = action.payload.date;
      state.appointmentTime = action.payload.time;
    },
    /**
     * Resets the checkout UI state.
     */
    resetCheckout: () => initialState,
  },
});

export const {
  setCurrentStep,
  setSelectedPaymentMethod,
  setIsProcessing,
  setServerErrors,
  setUseBillingForShipping,
  setCustomerNote,
  setAppointmentSelection,
  resetCheckout,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
